import { useState, useCallback } from 'react'
import axios from 'axios'
import { apiClient } from '@/libs/api_client'
import { useAuthStore } from '@/stores/auth'

interface RedisKeyInfo {
  key: string
  ttl: number
}

interface SearchKeysResult {
  pattern: string
  keys: RedisKeyInfo[]
  count: number
  total: number
  truncated: boolean
}

interface GetKeyResult {
  key: string
  value: string
  ttl: number
  type: string
}

interface DeleteKeysResult {
  deleted: number
  keys: string[]
}

export function useSystemToolsRedis() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const token = useAuthStore((state) => state.token)

  const getAuthHeaders = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {}
  }, [token])

  const searchKeys = useCallback(async (pattern: string): Promise<SearchKeysResult> => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await apiClient.get<{ success: boolean; data: SearchKeysResult }>(
        '/api/system-tools/redis/keys',
        { params: { pattern }, headers: getAuthHeaders() },
      )

      if (!response.data.success) {
        throw new Error('搜尋失敗')
      }

      return response.data.data
    } catch (err) {
      const message =
        (axios.isAxiosError<{ error?: { message?: string } }>(err)
          ? err.response?.data?.error?.message
          : undefined) || '搜尋 Redis keys 失敗'
      setError(message)
      throw new Error(message)
    } finally {
      setIsLoading(false)
    }
  }, [getAuthHeaders])

  const getKeyValue = useCallback(async (key: string): Promise<GetKeyResult> => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await apiClient.get<{ success: boolean; data: GetKeyResult }>(
        `/api/system-tools/redis/get/${encodeURIComponent(key)}`,
        { headers: getAuthHeaders() },
      )

      if (!response.data.success) {
        throw new Error('取得值失敗')
      }

      return response.data.data
    } catch (err) {
      const message =
        (axios.isAxiosError<{ error?: { message?: string } }>(err)
          ? err.response?.data?.error?.message
          : undefined) || '取得 Redis key 值失敗'
      setError(message)
      throw new Error(message)
    } finally {
      setIsLoading(false)
    }
  }, [getAuthHeaders])

  const deleteKeys = useCallback(async (keys: string[]): Promise<DeleteKeysResult> => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await apiClient.delete<{ success: boolean; data: DeleteKeysResult }>(
        '/api/system-tools/redis/keys',
        { data: { keys }, headers: getAuthHeaders() },
      )

      if (!response.data.success) {
        throw new Error('刪除失敗')
      }

      return response.data.data
    } catch (err) {
      const message =
        (axios.isAxiosError<{ error?: { message?: string } }>(err)
          ? err.response?.data?.error?.message
          : undefined) || '刪除 Redis keys 失敗'
      setError(message)
      throw new Error(message)
    } finally {
      setIsLoading(false)
    }
  }, [getAuthHeaders])

  return {
    searchKeys,
    getKeyValue,
    deleteKeys,
    isLoading,
    error,
  }
}
